import { TextLink } from '@/components/TextLink';
import { ProgressBar } from '../ProgressBar';
import { IconArrowLeft } from '@/components/icons';
import { Button, ChatButton } from '@/components/atoms/Button';
import {
	FormContext_Actions,
	FormContext_State,
	satifiesFormContext_State,
	useFormContext,
} from '../FormContext';
import { MouseEvent, useMemo } from 'react';
import { Alert } from '@/components/atoms/Alert';
import { trackEvent } from '@/helpers/heap/trackEvent';
import { getSplitNextStep } from '@/helpers/split/splitFormFlow';

export interface FormControlsProps {
	testId?: string;
	submitLabel?: string;
}

const FormControls = <
	State extends FormContext_State<Steps>,
	Action,
	Steps extends string,
>(
	props: FormControlsProps,
) => {
	const { testId = 'FormControls', submitLabel = 'Finish' } = props;
	const { state, submit, dispatch } = useFormContext<Steps, State, Action>();

	const { steps, activeStep, valid, totalSteps, currentStep, experience } =
		useMemo(() => {
			if (!satifiesFormContext_State<Steps>(state)) {
				throw new Error('State does not satisfy FormContext_State');
			}
			const {
				steps,
				activeStep,
				valid,
				maxSteps: totalSteps,
				currentStep,
				experience,
			} = state;
			return { steps, activeStep, valid, totalSteps, currentStep, experience };
		}, [state]);

	const step = useMemo(
		// Added a partial definition for the step object to fix TS error
		// The full type definition is in the FormContext file 'FormContext_Step<Steps>'.
		() => steps.find((s: { name: string }) => s.name === activeStep),
		[steps, activeStep],
	);
	if (!step) return null;

	const { onNext, name } = step;

	const handleSubmit = (event: MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();

		if (!valid) {
			dispatch({
				type: FormContext_Actions.SHOW_VALIDATION_ERRORS,
				payload: true,
			} as Action);
		}
		if (valid) {
			trackEvent('Form Finished', { experience: experience });
			submit(state, dispatch);
		}
	};

	const dismissAlert = () => {
		dispatch({ type: FormContext_Actions.SET_IDLE, payload: null } as Action);
	};

	const handleNext = () => {
		let stepToGoTo: string | null = null;
		// if there's a split config, get the next step from the split config
		if (state.splitConfig) {
			stepToGoTo = getSplitNextStep(activeStep, state.splitConfig);
			// otherwise, get the next step from the step's routing file
		} else if (onNext) {
			stepToGoTo = onNext(activeStep, state);
		}

		if (stepToGoTo) {
			trackEvent(`${name} Step`, {
				stepAction: 'Next',
				experience: experience,
			});
			// Had to cast as ActionA because TS was complaining about the fact
			// that during implementation of the FormContext, the extended type
			// can be overwritten with a different incompatible type.
			dispatch({
				type: FormContext_Actions.NEXT,
				payload: stepToGoTo,
			} as Action);
		}
	};

	const handleBack = () => {
		dispatch({ type: FormContext_Actions.BACK, payload: '' } as Action);
	};

	return (
		<div
			data-testid={testId}
			className="sticky bottom-0 z-15 mt-auto w-full bg-gradient-to-t from-white from-60% pt-15"
		>
			{state.requestStatus === 'REJECTED' && (
				<Alert type="danger">
					<div className="flex shrink items-center justify-between">
						<div>
							Something went wrong submitting your quote request. Please try
							again.
						</div>
						<div className="flex max-w-61 shrink-0 justify-end">
							<Button onClick={dismissAlert} variant="medium-default">
								Dismiss
							</Button>
						</div>
					</div>
				</Alert>
			)}
			<ProgressBar />
			<div className="hvac-container mx-auto my-3 flex justify-between lg:my-5">
				<div className="flex items-center justify-start">
					<div className="group mr-10">
						<TextLink
							onClick={handleBack}
							iconRender={(className) => (
								<IconArrowLeft className={className} />
							)}
							iconPosition="left"
							buttonData={{
								action: 'back',
								text: 'Back',
								location: `${name}`,
								testId: 'Button',
							}}
						>
							Back
						</TextLink>
					</div>
					<ChatButton variant="progress-bar"></ChatButton>
				</div>
				<div className="flex items-center justify-end">
					<label className="mr-4 min-w-max text-base text-slate-500 lg:mr-6">
						Step {currentStep + 1} / {totalSteps}
					</label>
					{currentStep + 1 < totalSteps && (
						<Button
							variant="medium-default"
							onClick={handleNext}
							disabled={!valid}
							data={{
								action: 'continue',
								text: 'Continue',
								location: name + '',
								testId: 'Button',
							}}
						>
							Continue
						</Button>
					)}
					{currentStep + 1 === totalSteps && (
						<Button
							variant="medium-default"
							onClick={handleSubmit}
							data={{
								action: 'submit',
								text: 'Finish',
								location: name + '',
								testId: 'Button',
							}}
						>
							{submitLabel}
						</Button>
					)}
				</div>
			</div>
		</div>
	);
};

export default FormControls;
